import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, TrendingUp, Eye } from 'lucide-react';
import { getLandmarkImage } from '../lib/landmarkImages';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Homepage "Trending in Paisley" tile.
 * Surfaces the most-viewed articles from the last 30 days so readers land on
 * what's currently hot — internal linking flywheel that boosts dwell time and
 * gives evergreen high-performers extra hits.
 *
 * Backed by GET /api/homepage/trending which uses time-stamped view events
 * and falls back to lifetime views for the bootstrap window.
 */
const TrendingInPaisley = ({ limit = 6, days = 30 }) => {
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | empty

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/api/homepage/trending?days=${days}&limit=${limit}`)
      .then((r) => r.json())
      .then((d) => {
        if (!mounted) return;
        const t = Array.isArray(d.trending) ? d.trending : [];
        setItems(t);
        setState(t.length ? 'ready' : 'empty');
      })
      .catch(() => mounted && setState('empty'));
    return () => { mounted = false; };
  }, [limit, days]);

  if (state === 'empty') return null;

  return (
    <section
      className="py-16 bg-gradient-to-b from-slate-50 to-white"
      data-testid="home-trending"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold uppercase tracking-[0.14em]">
              <Flame size={12} /> Trending
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mt-3"
              data-testid="home-trending-h2"
            >
              What Paisley&apos;s reading right now
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Most-viewed stories on paisley.org.uk over the last {days} days.
            </p>
          </div>
          <Link
            to="/news"
            className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700"
            data-testid="home-trending-all"
          >
            Browse all news <TrendingUp size={14} />
          </Link>
        </div>

        {state === 'loading' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: limit }).map((_, i) => (
              <div
                key={i}
                className="aspect-[16/10] bg-slate-100 animate-pulse rounded-xl"
                data-testid={`home-trending-skeleton-${i}`}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="home-trending-list">
            {items.map((p, i) => {
              // If the post has its own featured image, use that. Otherwise
              // pick a Paisley landmark from the rotation, seeded by the post
              // slug so the same card always shows the same landmark (no
              // flipping between renders).
              const landmark = getLandmarkImage(p.slug || p.id || String(i));
              const img = p.featured_image || landmark.url;
              const imgAlt = p.featured_image ? p.title : `${p.title} — ${landmark.alt}`;
              const isHero = i === 0;
              return (
                <Link
                  key={p.id || p.slug}
                  to={`/news/${p.slug}`}
                  className={`group relative overflow-hidden rounded-xl bg-white border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all ${
                    isHero ? 'col-span-2 md:col-span-1 md:row-span-2' : ''
                  }`}
                  data-testid={`home-trending-card-${p.slug}`}
                >
                  <div className={`relative w-full ${isHero ? 'aspect-[4/5] md:aspect-[3/4]' : 'aspect-[16/10]'}`}>
                    <img
                      src={img}
                      alt={imgAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-orange-500 px-2 py-1 rounded">
                        #{i + 1}
                      </span>
                      {p.category && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/90 bg-white/15 backdrop-blur-sm px-2 py-1 rounded">
                          {p.category}
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className={`font-bold text-white leading-tight ${isHero ? 'text-xl md:text-2xl' : 'text-base'}`}>
                        {p.title}
                      </h3>
                      {(p.recent_views > 0 || p.views > 0) && (
                        <div className="flex items-center gap-1 mt-2 text-[11px] text-white/80">
                          <Eye size={11} />
                          <span className="tabular-nums">
                            {(p.recent_views || p.views || 0).toLocaleString('en-GB')}
                          </span>
                          <span>{p.recent_views > 0 ? `in ${days}d` : 'lifetime'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default TrendingInPaisley;
